import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const CATEGORY_COLORS = {
  core: "#1E3A8A",
  communication: "#10B981",
  tools: "#F59E0B",
  finance: "#C5A059",
  recruitment: "#0F172A",
};

export default function FeatureTogglesPage() {
  const { user } = useAuth();
  const [toggles, setToggles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadToggles(); }, []);

  const loadToggles = async () => {
    try {
      const { data } = await api.get("/feature-toggles");
      setToggles(data);
    } catch { toast.error("Failed to load feature toggles"); }
    finally { setLoading(false); }
  };

  const handleToggle = async (key, currentState) => {
    try {
      await api.put(`/feature-toggles/${key}`, { enabled: !currentState });
      setToggles(prev => prev.map(t => t.key === key ? { ...t, enabled: !currentState } : t));
      toast.success(`Feature ${!currentState ? "enabled" : "disabled"}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update toggle"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-16 skeleton-shimmer rounded-lg" />)}</div>;

  const grouped = toggles.reduce((acc, t) => {
    const cat = t.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div data-testid="feature-toggles-page" className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Feature Toggles</h1>
        <p className="text-sm text-[#6B7280] mt-1">Enable or disable platform modules</p>
      </div>

      {Object.entries(grouped).map(([category, features]) => (
        <div key={category} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: CATEGORY_COLORS[category] || "#6B7280" }}>
            {category}
          </h2>
          <div className="space-y-2">
            {features.map(toggle => (
              <div key={toggle.key} className="bg-white rounded-lg border border-[#E5E7EB] px-5 py-4 flex items-center justify-between" data-testid={`toggle-${toggle.key}`}>
                <div>
                  <h3 className="text-sm font-semibold text-[#0F172A]">{toggle.label}</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">{toggle.description}</p>
                </div>
                {user?.role === "admin" ? (
                  <button onClick={() => handleToggle(toggle.key, toggle.enabled)} className="transition-colors" data-testid={`toggle-btn-${toggle.key}`}>
                    {toggle.enabled ? (
                      <ToggleRight size={36} weight="fill" className="text-[#10B981]" />
                    ) : (
                      <ToggleLeft size={36} weight="fill" className="text-[#D1D5DB]" />
                    )}
                  </button>
                ) : (
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${toggle.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {toggle.enabled ? "Active" : "Inactive"}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
